import { Button, Spinner, Stack, Typography } from '@locus-taxy/disco-ui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../../utils/testIdUtils';
import type { ProfileViewMode } from './profileTypes';

interface ProfileTitleActionsProps {
  mode: ProfileViewMode;
  canUpdate: boolean;
  editRestrictedReason?: string | null;
  isSaving: boolean;
  isDirty: boolean;
  /** Whether a save is held for approval instead of applied. */
  saveNeedsApproval: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
}

/** The account page title bar's actions: Edit (with why it is unavailable) in view mode, Cancel and Save in edit mode. */
export function ProfileTitleActions({
  mode,
  canUpdate,
  editRestrictedReason,
  isSaving,
  isDirty,
  saveNeedsApproval,
  onEdit,
  onCancel,
  onSave,
}: Readonly<ProfileTitleActionsProps>) {
  const { t } = useTranslation();

  return mode === 'view' ? (
    <Stack direction="column" gap={1} align="end">
      <Button
        size="lg"
        disabled={!canUpdate}
        onClick={onEdit}
        data-testid={getTestId({
          action: TestIdAction.VIEW,
          object: 'edit-profile-button',
          page: 'account',
        })}
      >
        {t('dashboard.userDetails.editProfile', 'Edit Profile')}
      </Button>
      {/* Four different conditions used to share one dead button and explain none of them. */}
      {!canUpdate && editRestrictedReason && (
        <Typography
          variant="caption"
          color="muted"
          className="max-w-xs text-right"
          data-testid={getTestId({
            action: TestIdAction.VIEW,
            object: 'edit-restricted-reason',
            page: 'account',
          })}
        >
          {editRestrictedReason}
        </Typography>
      )}
    </Stack>
  ) : (
    <Stack direction="column" gap={1} align="end">
      <Stack direction="row" gap={2}>
        <Button
          variant="outline"
          size="lg"
          disabled={isSaving}
          onClick={onCancel}
          data-testid={getTestId({
            action: TestIdAction.VIEW,
            object: 'cancel-button',
            page: 'account',
          })}
        >
          {t('dashboard.userDetails.cancel', 'Cancel')}
        </Button>
        <Button
          size="lg"
          disabled={isSaving || !isDirty}
          aria-busy={isSaving}
          onClick={onSave}
          data-testid={getTestId({
            action: TestIdAction.VIEW,
            object: 'save-changes-button',
            page: 'account',
          })}
        >
          {/* Covers the save and the refetch after it, so the button never looks idle mid-save. */}
          {isSaving && (
            <Spinner
              size="sm"
              className="mr-2"
              aria-hidden="true"
              aria-label=""
              data-testid={getTestId({
                action: TestIdAction.VIEW,
                object: 'save-changes-spinner',
                page: 'account',
              })}
            />
          )}
          {t('dashboard.userDetails.saveChanges', 'Save Changes')}
        </Button>
      </Stack>
      {/* Said before the save, not only after: the change will not show until approved. */}
      {saveNeedsApproval && (
        <Typography
          variant="caption"
          color="muted"
          className="max-w-xs text-right"
          data-testid={getTestId({
            action: TestIdAction.VIEW,
            object: 'save-needs-approval',
            page: 'account',
          })}
        >
          {t(
            'dashboard.userDetails.saveNeedsApproval',
            'Changes you save will be sent for approval.'
          )}
        </Typography>
      )}
    </Stack>
  );
}
